import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL;

const generateRandomPassword = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+[]{}|;:,.<>?';
  let password = '';
  for (let i = 0; i < 20; i++) {
    const randomIndex = Math.floor(Math.random() * chars.length);
    password += chars[randomIndex];
  }
  return password;
};

export default function GenerateUser() {
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [folder, setFolder] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [expirationTime, setExpirationTime] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // 1. Fetch existing users
      const usersRes = await fetch(`${API_URL}/get-users`);
      if (!usersRes.ok) throw new Error('Failed to fetch users.');
      const users = await usersRes.json();

      const userExists = users.some((u: any) => u.username.toLowerCase() === username.toLowerCase());
      if (userExists) throw new Error('Username already exists. Please choose a different one.');

      // 2. Validate folder
      if (!folder || folder.includes(' ')) {
        throw new Error('Invalid folder path. No spaces allowed.');
      }

      // 3. Calculate expiration
      let expiresIn = 0;
      if (expirationDate && expirationTime) {
        const selected = new Date(`${expirationDate}T${expirationTime}`);
        const now = new Date();
        expiresIn = Math.floor((selected.getTime() - now.getTime()) / 1000);
        if (expiresIn <= 0) throw new Error('Expiration must be in the future.');
      } else {
        expiresIn = 365 * 24 * 60 * 60; // 1 year
      }

      const password = generateRandomPassword();

      // 4. Submit user creation
      const response = await fetch(`${API_URL}/generate-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, folder, expiresIn, password })
      });

      if (!response.ok) throw new Error('Failed to generate user.');

      const data = await response.json();
      console.log('User created:', data);
      setSuccess('User generated successfully.');
      setTimeout(() => navigate('/admin'), 1500);
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen px-6 py-8 bg-gray-50 flex justify-center">
      <div className="max-w-xl w-full bg-white p-6 rounded-xl shadow">
        <h2 className="text-2xl font-bold mb-4">Generate New User</h2>

        {error && <div className="text-red-500 text-sm mb-4">{error}</div>}
        {success && <div className="text-green-500 text-sm mb-4">{success}</div>}

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="w-full px-3 py-2 border rounded"
            disabled={loading}
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Folder Path (omit "pharmaneststandard/")
          </label>
          <input
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
            placeholder="e.g. UploadLink_Test2/"
            className="w-full px-3 py-2 border rounded"
            disabled={loading}
          />
        </div>

        <div className="mb-6 grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expiration Date</label>
            <input
              type="date"
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.target.value)}
              className="w-full px-3 py-2 border rounded"
              disabled={loading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expiration Time</label>
            <input
              type="time"
              value={expirationTime}
              onChange={(e) => setExpirationTime(e.target.value)}
              className="w-full px-3 py-2 border rounded"
              disabled={loading}
            />
          </div>
        </div>

        <div className="flex justify-between items-center">
          <button
            onClick={() => navigate('/admin')}
            className="text-sm text-gray-600 hover:underline"
            disabled={loading}
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Generate User'}
          </button>
        </div>
      </div>
    </div>
  );
}
